import { EcommerceAPIApi as EcommerceApi, PaymentMethod } from '@parkingbase/ecommerce-api-test';
import { BaseApiService } from './baseApi.service';

export class PaymentMethodService extends BaseApiService {
  protected userApi: EcommerceApi;

  constructor() {
    super();
    this.userApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
  }

  async getAllPaymentMethods(): Promise<PaymentMethod[]> {
    const response = await this.userApi.getAllPaymentMethods();
    return response.data;
  }

  async getPaymentMethod(id: string): Promise<PaymentMethod> {
    const response = await this.userApi.getPaymentMethod(id);
    return response.data;
  }

  async createPaymentMethod(
    paymentMethod: PaymentMethod,
    recaptchaToken?: string
  ): Promise<PaymentMethod> {
    const options = recaptchaToken
      ? { headers: { 'g-recaptcha-response': recaptchaToken } }
      : undefined;
    const response = await this.userApi.createPaymentMethod(paymentMethod, options);
    return response.data;
  }

  async updatePaymentMethod(
    id: string,
    paymentMethod: PaymentMethod,
    recaptchaToken?: string
  ): Promise<PaymentMethod> {
    const options = recaptchaToken
      ? { headers: { 'g-recaptcha-response': recaptchaToken } }
      : undefined;
    const response = await this.userApi.updatePaymentMethod(id, paymentMethod, options);
    return response.data;
  }

  async deletePaymentMethod(id: string, recaptchaToken?: string): Promise<void> {
    const options = recaptchaToken
      ? { headers: { 'g-recaptcha-response': recaptchaToken } }
      : undefined;
    await this.userApi.deletePaymentMethod(id, options);
  }

  /**
   * Mints a Stripe ACH SetupIntent for the authenticated customer on the garage's
   * ACH connected account. Creating a SetupIntent needs the secret key, so it's a
   * server call (the only one) — it carries no bank data. The bank form confirms
   * the returned client secret with the bank details via stripe.confirmUsBankAccountSetup,
   * which records the mandate, verifies, and attaches the reusable pm to the Customer.
   * Uses the raw axios client because the generated client predates the endpoint.
   */
  async createStripeAchSetupIntent(
    garageId: string
  ): Promise<{ clientSecret: string; customerId: string }> {
    const response = await this.apiClient.post(
      '/user/paymentmethod/stripe/ach-setup-intent',
      null,
      { params: { garageId } }
    );
    return response.data;
  }

  /**
   * Reloads a Stripe SetupIntent so the customer can resume microdeposit verification
   * for a manually-added bank account. Returns the client secret for
   * stripe.verifyMicrodepositsForSetup plus the current status and microdeposit kind
   * (amounts / descriptor_code). Raw axios client — the endpoint postdates the generated client.
   */
  async getStripeSetupIntent(
    setupIntentId: string,
    garageId: string
  ): Promise<{ clientSecret: string; status: string; microdepositKind: string }> {
    const response = await this.apiClient.get(
      `/user/paymentmethod/stripe/setup-intent/${encodeURIComponent(setupIntentId)}`,
      { params: { garageId } }
    );
    return response.data;
  }

  /**
   * Re-derives + persists the ACH verification status for a saved bank account after the
   * customer confirms microdeposits, flipping PENDING → VERIFIED so it becomes chargeable
   * without waiting for the webhook. Raw axios client — endpoint postdates the generated client.
   */
  async refreshStripeAchStatus(
    paymentMethodId: string,
    garageId: string
  ): Promise<{ achVerificationStatus: string }> {
    const response = await this.apiClient.post(
      `/user/paymentmethod/stripe/payment-method/${encodeURIComponent(paymentMethodId)}/refresh-ach-status`,
      null,
      { params: { garageId } }
    );
    return response.data;
  }

  /**
   * Re-reads a saved ADYEN bank account's verification status (PB-2595 phase 2): PENDING until
   * Adyen's webhook has delivered the stored-payment-method token, then VERIFIED (or FAILED).
   * Raw axios client — the endpoint postdates the generated client.
   */
  async refreshAdyenAchStatus(
    paymentMethodId: string,
    garageId: string
  ): Promise<{ achVerificationStatus?: string }> {
    const response = await this.apiClient.post(
      `/user/paymentmethod/adyen/payment-method/${encodeURIComponent(paymentMethodId)}/refresh-ach-status`,
      null,
      { params: { garageId } }
    );
    return response.data;
  }

  /**
   * Mints a PayPal Vault v3 SETUP token for the add-card flow (PB-3067).
   * Creating a setup token requires the garage's PayPal REST secret, so it's a
   * server call — it carries no card data. The PayPal CardFields confirm the
   * returned token client-side; the save-payment-method call then submits the
   * confirmed setup-token id as `paymentToken` and the API exchanges it for the
   * permanent vault token. Raw axios client — the endpoint postdates the
   * generated client. NOTE: 404 here is expected until the api-side endpoint
   * lands; the error is propagated (never swallowed) so callers surface it.
   */
  async createPaypalSetupToken(garageId: string): Promise<{ id: string }> {
    const response = await this.apiClient.post(
      `/v2/garages/${encodeURIComponent(garageId)}/paypal/setup-token`,
      null
    );
    return response.data;
  }

  /**
   * Set a payment method as default (primary).
   * The backend handles unsetting the previous default automatically.
   */
  async setDefaultPaymentMethod(
    id: string,
    currentPaymentMethods: PaymentMethod[],
    recaptchaToken?: string
  ): Promise<void> {
    const paymentMethod = currentPaymentMethods.find((pm) => pm.id === id);
    if (paymentMethod) {
      await this.updatePaymentMethod(
        id,
        { ...paymentMethod, defaultPaymentMethod: true },
        recaptchaToken
      );
    }
  }
}
